import { useEffect, useState } from "react";
import useAuth from "../../hooks/useAuth";
import Paginator from "../exercise/components/Paginator";
import NewAssignment from "../new_assignment/NewAssignment";
import Button from "../../components/Button";
import ExitButton from "../../components/ExitButton";

const ClassInterface = ({successHandler, course, teacher = ""}) => {
    const {auth, setAuth} = useAuth();
    const [students, setStudents] = useState([]);
    const [studentPages, setStudentPages] = useState(0);
    const [studentPage, setStudentPage] = useState(1);
    const [assignments, setAssignments] = useState([]);
    const [chats, setChats] = useState([]);
    const [chat, setChat] = useState("");
    const [changeList, setChangeList] = useState([]);
    const [student, setStudent] = useState("");
    const [assignmentPage, setNewAssign] = useState(false);
    const [tagError, setTagError] = useState(null);

    const getTeacher = () => {
        return course.split("/")[0];
    }

    const getTag = () => {
        return course.split("/")[1];
    }

    const isTeacher = auth?.username === getTeacher() || teacher === getTeacher();
    const tag = getTag();

    const [newTag, setNewTag] = useState(tag);

    const fetchData = async (route, method) => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/teacher/${route}` + ((teacher !== "") ? `?teacher_name=${teacher}` : ""), {
            method: method,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
            return null;
        }
        if (response.ok) {
            const json = await response.json();
            return json;
        }
        return null;
    }

    const getStudents = async (page = 1) => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/course/students/${course}/${page}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.ok) {
            const json = await response.json();
            setStudents(json);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const leaveClass = async () => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/leave-class/${course}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.ok) {
            successHandler("");
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }
    
    const getStudentPages = async () => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/course/student-pages/${course}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.ok) {
            const json = await response.json();
            setStudentPages(json.pages);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const getAssignments = async () => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/course/assignments/${course}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.ok) {
            const json = await response.json();
            setAssignments(json);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const getChats = async () => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/course/chats/${course}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.ok) {
            const json = await response.json();
            setChats(json);
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const sendChat = async () => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/course/chat/${course}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({text: chat})
        });
        if (response.ok) {
            getChats();
            setChat("");
        }
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
        }
    }

    const checkChat = async () => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/course/chat-count/${course}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.ok) {
            const json = await response.json();
            if (json.count !== chats.length) {
                getChats();
            }
        }
    }

    const addChange = (change) => {
        setChangeList([...changeList, change]);
    }

    const removeChange = (i) => {
        let array = [...changeList];
        array.splice(i, 1);
        setChangeList(array);
    }

    const addAddStudent = () => {
        addChange(`ADD ${student}`);
        setStudent("");
    }

    const addRemoveStudent = (name) => {
        addChange(`REMOVE ${name}`);
    }

    const addDeleteClass = () => {
        addChange("DELETE");
    }

    const addUpdateTag = () => {
        addChange(`RENAME ${newTag}`);
    }

    const addStudent = async (name) => {
        await fetchData(`add-user/${tag}/${name}`, "POST");
    }

    const removeStudent = async (name) => {
        await fetchData(`remove-user/${tag}/${name}`, "DELETE");
    }

    const deleteClass = async () => {
        await fetchData(`delete-class/${tag}`, "DELETE");
    }

    const updateTag = async (t) => {
        await fetchData(`update-tag/${tag}/${t}`, "POST");
    }

    const commit = async () => {
        for (const change of changeList) {
            const splits = change.split(" ");
            switch (splits[0]) {
                case "ADD":
                    await addStudent(splits[1]);
                    break;
                case "REMOVE":
                    await removeStudent(splits[1]);
                    break;
                case "DELETE":
                    await deleteClass();
                    break;
                case "RENAME":
                    await updateTag(splits[1]);
                    break;
            }
        }
        setChangeList([]);
        getStudents();
        getAssignments();
    }

    const newAssignHandler = (state) => {
        setNewAssign(state);
        getAssignments();
    }

    const isTagChanged = (t = newTag) => {
        return t !== tag && t !== "";
    }

    const getTagError = async (t = newTag) => {
        if (t === "") {
            return "Name of class is required.";
        }
        for (let c of [" ", "/"]) {
            if (t.includes(c)) {
                return `Name of class cannot include \"${c}\".`;
            }
        }
        const json = await fetchData(`tag-exists/${t}`, "GET");
        if (json.exists && t !== tag) {
            return "You already have a class with this name";
        }
        return null;
    }

    const changeNewTag = async (t) => {
        setNewTag(t);
        setTagError(null);
        const err = await getTagError(t);
        if (err) {
            setTagError(err);
        }
    }
    
    const canUpdateTag = () => {
        return isTagChanged() && !tagError;
    }

    useEffect(() => {getStudents()}, []);
    useEffect(() => {getStudentPages()}, []);
    useEffect(() => {getStudents(studentPage)}, [studentPage]);
    useEffect(() => {getAssignments()}, []);
    useEffect(() => {getChats()}, []);
    useEffect(() => {checkChat()});

    if (assignmentPage) {
        return <NewAssignment successHandler={state => newAssignHandler(state)} tag={tag} teacher={teacher} />
    }

    return <div>
        <ExitButton func={() => {successHandler("")}} />
        <div className="flex flex-col">
            {isTeacher ? <form className="flex flex-row">
                <div className="flex flex-col">
                    <input className="border" type="text" value={newTag} onChange={e => changeNewTag(e.target.value)} />
                    {tagError && <div className="text-red-500">{tagError}</div>}
                </div>
                {canUpdateTag() && <Button text="Update" func={addUpdateTag} />}
            </form> : <h1>{course}</h1>}
            <div className="flex flex-row justify-between">
                <div className="flex flex-col">
                    <div className="flex flex-col border border-4">
                        <b>Students:</b>
                        <Paginator maxpage={studentPages} 
                            page={studentPage}
                            clickHandler={page => setStudentPage(page)}
                        />
                        {students.map(student => <div className="flex flex-row justify-between">
                            <div>{student}</div>
                            {isTeacher && <Button text="Remove" func={() => addRemoveStudent(student)}/>}
                        </div>)}
                    </div>
                    {isTeacher && <form className="flex flex-row">
                        <input
                            className="border"
                            type="text"
                            placeholder="Username/email"
                            value={student}
                            onChange={e => setStudent(e.target.value)}
                        />
                        <Button text="Invite" func={addAddStudent} />
                    </form>}
                </div>
                <div className="flex flex-col border border-4">
                    <b>Assignments</b>
                    {assignments.map(assignment => <div>{assignment}</div>)}
                    {isTeacher && <Button text="New assignment" func={() => setNewAssign(true)} />}
                </div>
                <div className="flex flex-col border border-4">
                    <b>Chat</b>
                    {chats.map(chat => <div className="flex flex-col border">
                        <div className="text-xs">{chat.user}</div>
                        <div>{chat.text}</div>
                        <div className="text-xs">{chat.time}</div>
                    </div>)}
                    <form className="flex flex-row">
                        <input
                            className="border"
                            type="text"
                            value={chat}
                            onChange={(e) => setChat(e.target.value)}
                        />
                        <Button text="Send" func={sendChat} />
                    </form>
                </div>
            </div>
            {isTeacher && <div>
                {(changeList?.length>0) && <div className="flex flex-col mx-auto border-2 border-gray-200 bg-gray-50 p-5 my-5">
                    {changeList.map((change, i) => <div className="grow flex flex-row gap-2 mx-auto">
                        <div className="text-md text-gray-400">{change}</div>
                        <Button text="Remove" func={() => removeChange(i)}/>
                    </div>)}
                    <Button text="Commit" func={commit} />
                </div>}
            </div>}
        </div>
        {isTeacher ? <Button
            text="Delete class"
            func={addDeleteClass}
        /> : <Button
            text="Leave class"
            func={leaveClass}
        />}
    </div>
}

export default ClassInterface;